import { Model } from './Model';
import { Memory } from './Memory';
import { GameEnv } from './GameEnv';

import { tensor2d, Tensor2D } from "@tensorflow/tfjs";

export type RenderCallback = (state: number[], score: number, won: boolean, lost: boolean) => void;

export class Orchestrator {
	private readonly model: Model;
	private memory: Memory;
	private steps: number;
	private readonly maxStepsPerGame: number;
	private readonly env: GameEnv;
	private readonly round_waiting: number;
	private readonly disable_wait: boolean;
	private readonly onRender: RenderCallback | null;
	private readonly trainEvery: number = 10;
	private readonly stepPenalty: number = -1;
	private discountRate: number = 0.95;
	private _stopRequested: boolean = false;

	get stopRequested(): boolean {
		return this._stopRequested;
	}

	requestStop() {
		this._stopRequested = true;
	}

	constructor(env: GameEnv, model: Model, memory: Memory, maxStepsPerGame: number, round_waiting: number, disable_wait: boolean, onRender?: RenderCallback) {
		this.model = model;
		this.memory = memory;
		this.env = env;

		this.steps = 0;
		this.maxStepsPerGame = maxStepsPerGame;

		this.round_waiting = round_waiting;
		this.disable_wait = disable_wait;
		this.onRender = onRender ?? null;
	}

	readonly encodeLogMax: number = 11;

	encodeState(state: number[]): number[] {
		const encoded = new Array<number>(state.length * (this.encodeLogMax + 1)).fill(0);
		for (let i = 0; i < state.length; i++) {
			const channel = state[i] === 0 ? 0 : Math.min(Math.log2(state[i]), this.encodeLogMax);
			encoded[i * (this.encodeLogMax + 1) + channel] = 1;
		}
		return encoded;
	}

	encodedStateSize(): number {
		return this.env.state.length * (this.encodeLogMax + 1);
	}

	private render() {
		if (this.onRender) {
			this.onRender([...this.env.state], this.env.score, this.env.won, this.env.lost);
		}
	}

	computeReward(scoreDelta: number, lost: boolean, won: boolean): number {
		let reward = scoreDelta + this.stepPenalty;
		if (won) reward += 100;
		if (lost) reward -= 100;
		return reward;
	}

	actionToAction(action: number): "left" | "right" | "up" | "down" {
		switch (action) {
			case 0:
				return "up"
			case 1:
				return "down"
			case 2:
				return "right"
			case 3:
				return "left"
		}
		return "up"
	}

	sleep(time: number) {
		return new Promise<void>((resolve) => {
			setTimeout(() => resolve(), time)
		})
	}

	async just_play(): Promise<boolean[]> {
		const state = this.model.makeInputTensor([this.encodeState(this.env.state)]);
		const action = this.model.chooseAction(state, this.env.legalMoves(), true);
		state.dispose();
		this.env.step(this.actionToAction(action));
		this.render();
		return [this.env.lost, this.env.won]
	}

	async run() {
		this._stopRequested = false;
		let encodedState = this.encodeState(this.env.state);
		let totalReward = 0;
		let step = 0;
		let movesThisGame = 0;
		while (step < this.maxStepsPerGame) {
			if (this._stopRequested) {
				console.log("Training interrupted");
				break;
			}
			const stateTensor = this.model.makeInputTensor([encodedState]);
			const action = this.model.chooseAction(stateTensor, this.env.legalMoves());
			stateTensor.dispose();

			const result = this.env.step(this.actionToAction(action));
			const done = this.env.won || this.env.lost;

			let reward: number;
			let nextEncodedState: number[];
			if (!result.moved) {
				reward = -1;
				nextEncodedState = encodedState;
			} else {
				movesThisGame++;
				reward = this.computeReward(result.gained, result.lost, result.won);
				nextEncodedState = this.encodeState(this.env.state);
			}

			this.memory.addSample([encodedState, action, reward, done ? null : nextEncodedState]);

			encodedState = nextEncodedState;
			totalReward += reward;
			this.steps += 1;
			step += 1;

			if (this.steps % this.trainEvery === 0 && this.memory.length >= this.model.batchSize) {
				await this.replay();
			}

			if (step == this.maxStepsPerGame) {
				console.log("Achieved reward: " + totalReward);
				break;
			}
			if (done) {
				console.log(`Game finished in ${movesThisGame} moves (score ${this.env.score}, ${this.env.won ? "won" : "lost"})`);
				movesThisGame = 0;
				this.env.reset();
				encodedState = this.encodeState(this.env.state);
			}
			if (!this.disable_wait) {
				this.render();
				await this.sleep(this.round_waiting);
			} else if (step % 50 === 0) {
				this.render();
			}
		}
		await this.replay()
		this.env.reset();
		this.render();
	}

	async replay() {
		const batch = this.memory.sample(this.model.batchSize);
		if (batch.length === 0) {
			return;
		}

		const numActions = this.model.numActions;

		const stateTensor = this.model.makeInputTensor(batch.map(([state]) => state));
		const qTensor = this.model.predict(stateTensor) as Tensor2D;
		const qValues = await qTensor.data();
		stateTensor.dispose();
		qTensor.dispose();

		const nonTerminalIndices: number[] = [];
		for (let i = 0; i < batch.length; i++) {
			if (batch[i][3] !== null) {
				nonTerminalIndices.push(i);
			}
		}

		const maxNextQ = new Float64Array(batch.length);
		if (nonTerminalIndices.length > 0) {
			const nextStates = nonTerminalIndices.map(i => batch[i][3]!);
			const nextStateTensor = this.model.makeInputTensor(nextStates);
			// Double DQN: online net selects the action, target net evaluates it
			const onlineQTensor = this.model.predict(nextStateTensor) as Tensor2D;
			const onlineQValues = await onlineQTensor.data();
			const targetQTensor = await this.model.predictTarget(nextStateTensor) as Tensor2D;
			const targetQValues = await targetQTensor.data();
			nextStateTensor.dispose();
			onlineQTensor.dispose();
			targetQTensor.dispose();
			nonTerminalIndices.forEach((batchIndex, k) => {
				let bestAction = 0;
				for (let a = 1; a < numActions; a++) {
					if (onlineQValues[k * numActions + a] > onlineQValues[k * numActions + bestAction]) {
						bestAction = a;
					}
				}
				maxNextQ[batchIndex] = targetQValues[k * numActions + bestAction];
			});
		}

		const x: number[][] = [];
		const y: number[][] = [];
		batch.forEach(([state, action, reward, nextState], i) => {
			const targets = Array.from(qValues.slice(i * numActions, (i + 1) * numActions));
			targets[action] = nextState === null
				? reward
				: reward + this.discountRate * maxNextQ[i];
			x.push(state);
			y.push(targets);
		});

		const xTensor = this.model.makeInputTensor(x);
		const yTensor = tensor2d(y, [y.length, numActions]);

		await this.model.train(xTensor, yTensor);

		xTensor.dispose();
		yTensor.dispose();
	}
}
